
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Home, Mail, ThumbsUp, Info, Link as LinkIcon } from "lucide-react";
import { motion } from "framer-motion";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { LainaCta } from "@/components/common/LainaCta";
import { siteMetadata } from "@/config/siteMetadata";
import OptimizedImage from "@/components/common/OptimizedImage";
import PartnersCarousel from "@/components/products/PartnersCarousel";

const ThankYouPage = () => {
  const location = useLocation();
  const { trackEvent } = useAnalytics();
  const queryParams = new URLSearchParams(location.search);
  const source = queryParams.get("source") || "contact";
  
  useEffect(() => {
    // Track page view with source information
    trackEvent("thank_you_page_view", { 
      source: source
    });
  }, [source, trackEvent]);
  
  // Determine message based on source
  const getContent = () => {
    switch(source) {
      case "newsletter":
        return {
          title: "Kiitos tilauksestasi!",
          message: "Olet nyt tilannut uutiskirjeemme. Lähetimme sinulle vahvistuksen sähköpostiisi. Saat jatkossa ajankohtaista tietoa rahoitusratkaisuista ja tarjouksista.",
          icon: "mail",
          cta: "Tutustu lainatuotteisiin",
          ctaLink: "/lainatuotteet",
          secondaryCta: "Kilpailuta lainasi",
          secondaryCtaLink: "/kilpailuta"
        };
      case "application":
        return {
          title: "Kiitos lainahakemuksestasi!",
          message: "Hakemuksesi on vastaanotettu. Käsittelemme sen mahdollisimman pian ja olemme sinuun yhteydessä sähköpostitse. Voit tutustua alla oleviin artikkeleihin, jotka voivat auttaa sinua lainaprosessissa.",
          icon: "application",
          cta: "Tutustu lainan ottamisen oppaaseen",
          ctaLink: "/artikkelit/lainan-ottamisen-opas-2025",
          secondaryCta: "Palaa etusivulle",
          secondaryCtaLink: "/"
        };
      case "contact":
      default:
        return {
          title: "Kiitos yhteydenotostasi!",
          message: "Viestisi on vastaanotettu. Vastaamme sinulle mahdollisimman pian, tavallisesti 1-2 arkipäivän kuluessa. Sillä välin voit tutustua alla oleviin suosittuihin artikkeleihin tai palveluihimme.",
          icon: "contact",
          cta: "Tutustu lainatuotteisiin",
          ctaLink: "/lainatuotteet",
          secondaryCta: "Kilpailuta lainasi",
          secondaryCtaLink: "/kilpailuta"
        };
    }
  };
  
  const content = getContent();
  
  return (
    <>
      <Helmet>
        <title>Kiitos yhteydenotostasi | Lainasampo.fi</title>
        <meta name="description" content="Kiitos yhteydenotostasi Lainasampo.fi palveluun. Olemme vastaanottaneet viestisi ja olemme sinuun yhteydessä pian." />
        <meta name="robots" content="noindex" />
      </Helmet>
      
      <div className="bg-gradient-to-b from-blue-50 to-white min-h-screen py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl shadow-md overflow-hidden"
          >
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 py-8 px-6">
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="flex justify-center mb-4"
              >
                <div className="bg-white/20 rounded-full p-4">
                  <CheckCircle className="h-12 w-12 text-white" />
                </div>
              </motion.div>
              <h1 className="text-2xl md:text-3xl font-bold text-center text-white">
                {content.title}
              </h1>
            </div>
            
            <div className="p-6 md:p-8">
              <p className="text-gray-700 text-center mb-8 text-lg">
                {content.message}
              </p>
              
              {/* Call to action buttons */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
              >
                <LainaCta 
                  to={content.ctaLink}
                  text={content.cta}
                  variant="primary"
                  size="md"
                  className="w-full sm:w-auto"
                  trackingLabel="thank_you_primary_cta"
                  location="thank_you_page"
                />
                
                <LainaCta
                  to={content.secondaryCtaLink}
                  text={content.secondaryCta}
                  variant="outline"
                  size="md"
                  className="w-full sm:w-auto"
                  trackingLabel="thank_you_secondary_cta"
                  location="thank_you_page"
                />
              </motion.div>
              
              {/* Kumppanit section - Using PartnersCarousel component */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="mb-8"
              >
                <PartnersCarousel />
              </motion.div>
              
              {/* Recommended articles section */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="bg-blue-50 rounded-lg p-6 mb-6"
              >
                <h2 className="text-lg font-semibold text-blue-700 mb-3 flex items-center">
                  <Info className="mr-2 h-5 w-5" />
                  Suosituimmat artikkelimme
                </h2>
                <ul className="space-y-3">
                  <li>
                    <Link to="/artikkelit/lainan-ottamisen-opas-2025" className="text-blue-600 hover:underline flex items-center">
                      <ArrowRight className="mr-2 h-4 w-4 flex-shrink-0" />
                      <span>Lainan ottamisen opas 2025</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/artikkelit/yhdistelylaina-opas" className="text-blue-600 hover:underline flex items-center">
                      <ArrowRight className="mr-2 h-4 w-4 flex-shrink-0" />
                      <span>Yhdistelylaina opas</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/artikkelit/miten-valita-oikea-laina-eri-tilanteisiin-2025" className="text-blue-600 hover:underline flex items-center">
                      <ArrowRight className="mr-2 h-4 w-4 flex-shrink-0" />
                      <span>Miten valita oikea laina eri tilanteisiin</span>
                    </Link>
                  </li>
                </ul>
              </motion.div>
              
              {/* Contact information - Updated with only email and website */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="bg-gray-50 rounded-lg p-6"
              >
                <h2 className="text-lg font-semibold text-gray-700 mb-3">Ota yhteyttä</h2>
                <div className="space-y-2">
                  <p className="flex items-center text-gray-600">
                    <Mail className="mr-2 h-4 w-4 text-blue-600" />
                    <span>{siteMetadata.companyInfo.email}</span>
                  </p>
                  <p className="flex items-center text-gray-600">
                    <LinkIcon className="mr-2 h-4 w-4 text-blue-600" />
                    <a href={siteMetadata.siteUrl} className="text-blue-600 hover:underline">{siteMetadata.siteUrl.replace('https://', '')}</a>
                  </p>
                </div>
              </motion.div>
              
              {/* Navigation home button at bottom */}
              <div className="mt-8 text-center">
                <Link 
                  to="/"
                  className="inline-flex items-center text-blue-600 hover:underline"
                >
                  <Home className="mr-2 h-5 w-5" />
                  Siirry etusivulle
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default ThankYouPage;
